import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { RequireAuth } from "../../../RequireAuth";
import TypeIcon from "../HubSpokeIcon/TypeIcon";
import { FieldGrid, InfoCard, SectionTitle, TextCard } from "./DataDisplay";
import { parseDeviceMeta } from "./reportUtils";

const SUMMARY_LAYOUT = {
  short: {
    cardVariant: "plain",
    containerProps: { spacing: 2, justifyContent: "space-between" },
    itemProps: { xs: 12, sm: 6, md: 3, sx: { cursor: "default" } },
    fieldProps: {
      stacked: true,
      skeleton: "wave",
      labelSx: { fontSize: "14px" },
    },
  },
  full: {
    cardVariant: "outlined",
    title: "Test Summary",
    containerProps: {
      spacing: 4,
      alignItems: "center",
      sx: { justifyContent: "space-between" },
    },
    itemProps: { xs: 12, sm: 6, md: 4, lg: 2, sx: { cursor: "default" } },
    fieldProps: {
      valueProps: { variant: "body2", color: "var(--app-text)" },
      valueSx: {
        mt: 0.2,
        wordBreak: "break-word",
        lineHeight: 1.2,
      },
    },
  },
};

const ECG_TYPE_COLORS = {
  Normal: "#348530",
  Abnormal: "#E4841A",
  Critical: "#D34848",
  Retake: "#4369CE",
  Beyond_critical: "#D34848",
};

const capitalize = (word) =>
  word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : "";

const buildSummaryItems = (type, data, navigate) => {
  const centerField = {
    label: "CENTER",
    value: (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <TypeIcon
          type={data?.center_details?.centerType}
          size={14}
          color="var(--app-text)"
        />
        <span
          style={{
            textDecoration: "underline",
          }}
        >
          {data?.center_details?.centerName || "--"}
        </span>
      </Box>
    ),
    onClick: () =>
      navigate(`/settings/centres/${data?.center_details?.center_id}`),
  };
  const openPatient = () => navigate(`/patients/${data?.user_data?._id}`);

  if (type === "short") {
    return [
      { label: "COLLECTION ID", value: "#" + data?.collection_id },
      {
        label: "NAME",
        value:
          data?.user_data?.first_name + " " + data?.user_data?.last_name,
        onClick: openPatient,
      },
      { label: "PATIENT ID", value: data?.user_data?.patient_id },
      centerField,
    ];
  }

  return [
    {
      label: "NAME",
      value:
        [data?.user_data?.first_name, data?.user_data?.last_name]
          .filter(Boolean)
          .join(" ") || "--",
      onClick: openPatient,
    },
    { label: "PATIENT ID", value: data?.user_data?.patient_id || "--" },
    {
      label: "AGE",
      value:
        data?.user_data?.userAge != null
          ? `${data.user_data.userAge} `
          : "--",
    },
    { label: "GENDER", value: data?.user_data?.gender || "--" },
    centerField,
  ];
};

export const PatientSummaryCard = ({ type = "full", data, loading = false }) => {
  const navigate = useNavigate();
  const layout = SUMMARY_LAYOUT[type] || SUMMARY_LAYOUT.full;

  const card = (
    <InfoCard variant={layout.cardVariant}>
      <FieldGrid
        loading={loading}
        containerProps={layout.containerProps}
        itemProps={layout.itemProps}
        fieldProps={layout.fieldProps}
        items={buildSummaryItems(type, data, navigate)}
      />
    </InfoCard>
  );

  if (!layout.title) return card;

  return (
    <Box sx={{ mt: 2 }}>
      <SectionTitle>{layout.title}</SectionTitle>
      {card}
    </Box>
  );
};

export const Interpretation = ({ type, data }) => {
  const ecgType = capitalize(
    data?.conclusions?.ecg_type ?? data?.interpretation_report_status,
  );
  const ecgColor = ECG_TYPE_COLORS[ecgType] || "#222";

  if (data?.interpretation_request_status != "completed") {
    return <Box sx={{ width: "100%" }} />;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box mb={2} mt={2}>
        <SectionTitle>Interpretation</SectionTitle>

        <InfoCard
          variant="paper"
          sx={{ borderRadius: "7px", mt: type === "short" ? 1 : 1.5 }}
        >
          <Typography
            sx={{ fontSize: "17px", fontWeight: 800, color: ecgColor }}
          >
            {ecgType?.split("_")?.map(capitalize)?.join(" ")} ECG
          </Typography>
        </InfoCard>
      </Box>

      <TextCard
        mb={2}
        title="Findings"
        cardSx={{ mt: 1 }}
        text={
          data?.interpretation_data?.findings?.length > 0
            ? data.interpretation_data.findings.join(", ")
            : "--"
        }
      />

      <TextCard
        title="Suggested Actions"
        cardSx={{ mt: 1 }}
        text={
          data?.interpretation_data?.suggested_actions?.trim()
            ? data.interpretation_data.suggested_actions
              .split("\n")
              .filter((line) => line.trim() !== "")
              .join(", ")
            : "--"
        }
      />
    </Box>
  );
};

const vitalValue = (vital) =>
  Number(vital?.value) > 0 ? `${vital.value} ${vital.unit ?? ""}` : "--";

export const PatientVitalsCard = ({ data, loading }) => {
  const vitals = data?.body_vitals;
  const bp = vitals?.blood_pressure;

  return (
    <Box>
      <SectionTitle>Patient Vitals</SectionTitle>

      <InfoCard>
        <FieldGrid
          loading={loading}
          containerProps={{
            spacing: 8,
            sx: { display: "flex", justifyContent: "space-between" },
          }}
          fieldProps={{
            valueProps: { variant: "body2" },
            valueSx: { mt: 0.5, fontSize: undefined },
          }}
          items={[
            { label: "HEIGHT", value: vitalValue(vitals?.height) },
            { label: "WEIGHT", value: vitalValue(vitals?.weight) },
            { label: "SPO2", value: vitalValue(vitals?.spo2) },
            {
              label: "BP",
              value:
                bp?.systolic > 0 && bp?.diastolic > 0
                  ? `${bp.systolic}/${bp.diastolic} ${bp.unit ?? ""}`
                  : "--",
            },
            { label: "TEMP", value: vitalValue(vitals?.temperature) },
            { label: "BLOOD GLUCOSE", value: vitalValue(vitals?.blood_sugar) },
            {
              label: "RESPIRATORY RATE",
              value: vitalValue(vitals?.respiratory_rate),
            },
          ]}
        />
      </InfoCard>
    </Box>
  );
};

export const CollectionDetailsCard = ({ data, loading }) => {
  const firstName = data?.assignee_details?.firstName || "";
  const lastName = data?.assignee_details?.lastName || "";

  return (
    <Box sx={{ mt: 2 }}>
      <SectionTitle>Collection Details</SectionTitle>

      <InfoCard sx={{ p: 2 }}>
        <FieldGrid
          loading={loading}
          containerProps={{
            sx: {
              flexDirection: { xs: "column", md: "row" },
              rowGap: { xs: 1.5, md: 0 },
              columnGap: { md: 6 },
              justifyContent: "space-between",
            },
          }}
          itemProps={{ xs: 12, md: "auto" }}
          fieldProps={{
            valueProps: { variant: "body2" },
            valueSx: { mt: 0.5, fontSize: { xs: "14px", md: "16px" } },
          }}
          items={[
            {
              label: "DEVICE",
              value: parseDeviceMeta(data?.meta_data).deviceName,
            },
            {
              label: "COLLECTED BY",
              value:
                firstName || lastName
                  ? `${firstName} ${lastName}`.trim()
                  : "--",
            },
            {
              label: "COLLECTED ON",
              value: data?.report_timestamp_readable
                ? moment
                  .utc(data.report_timestamp_readable)
                  .tz("Asia/Kolkata")
                  .format("lll")
                : "--",
            },
          ]}
        />
      </InfoCard>
    </Box>
  );
};

export const SymptomsCard = ({ data }) => {
  if (!(data?.symptoms?.length > 0)) return null;

  return (
    <Box mt={2}>
      <SectionTitle>Symptoms</SectionTitle>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mt: 1,
          backgroundColor: "var(--app-surface)",
          border: "1px solid var(--app-border)",
          borderRadius: "12px",
          p: "17px",
        }}
      >
        {data?.symptoms?.map((symptom, index) => (
          <Chip
            key={index}
            label={symptom}
            sx={{
              px: 1.5,
              height: 44,
              borderRadius: "7px",
              backgroundColor: "var(--app-surface-2)",
              color: "var(--app-text)",
              fontSize: "15px",
              fontWeight: 500,
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export const SendForInterpretationAccess = ({ children }) => (
  <>
    <RequireAuth role={["b2b"]}>{children}</RequireAuth>
    <RequireAuth
      role={["b2b-member", "b2b-admin"]}
      permission={"canSendForInterpretation"}
    >
      {children}
    </RequireAuth>
  </>
);
